import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import DependantCard from '../../components/shared/DependantCard';
import GoogleMapLocationPicker from '../../components/shared/GoogleMapLocationPicker';
import { Button } from '../../components/ui/button';
import { Edit3, MapPin, UserPlus } from 'lucide-react';
import { getApiErrorMessage } from '../../lib/apiError';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';

const hasHomePin = (dependant) => Boolean(
  dependant?.address?.coordinates &&
  (
    dependant.address.coordinates.latitude !== undefined ||
    dependant.address.coordinates.lat !== undefined
  ) &&
  (
    dependant.address.coordinates.longitude !== undefined ||
    dependant.address.coordinates.lng !== undefined
  )
);

const addressHintFor = (dependant) => {
  const address = dependant?.address || {};
  return [
    address.street,
    address.neighbourhood,
    address.landmark,
    address.lga || address.city,
    address.state,
    address.country || 'Nigeria',
  ].filter(Boolean).join(', ');
};

const DependantsPage = () => {
  const { authApi } = useAuth();
  const { role, currentUser, dependants: allDependants, getSubscriberDependants, fetchData, upsertDependant } = useApp();
  const dependants = role === 'subscriber'
    ? getSubscriberDependants(currentUser?.profileId || currentUser?.id)
    : allDependants;
  const [selectedDependant, setSelectedDependant] = useState(null);
  const [locationDraft, setLocationDraft] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [detailUserId, setDetailUserId] = useState('');
  const missingLocationPins = useMemo(() => dependants.filter(dependant => !hasHomePin(dependant)), [dependants]);

  const openLocationModal = (dependant) => {
    setSelectedDependant(dependant);
    setLocationDraft({
      coordinates: dependant.address?.coordinates || null,
      formattedAddress: dependant.address?.formattedAddress || dependant.address?.formatted_address || addressHintFor(dependant),
      placeId: dependant.address?.placeId || dependant.address?.place_id || '',
    });
  };

  const saveLocation = async () => {
    if (!selectedDependant || !locationDraft?.coordinates) {
      toast.error('Set a home pin before saving.');
      return;
    }
    setSavingLocation(true);
    try {
      const response = await authApi.patch(`/dependants/${selectedDependant.id}/location`, {
        latitude: locationDraft.coordinates.latitude ?? locationDraft.coordinates.lat,
        longitude: locationDraft.coordinates.longitude ?? locationDraft.coordinates.lng,
        formatted_address: locationDraft.formattedAddress || '',
        place_id: locationDraft.placeId || '',
        location_source: locationDraft.locationSource || 'google_maps_picker',
        location_confirmed_at: locationDraft.locationConfirmedAt || new Date().toISOString(),
      });
      upsertDependant(response.data);
      await fetchData({ force: true });
      toast.success('Home location confirmed.');
      setSelectedDependant(null);
      setLocationDraft(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save home location.'));
    } finally {
      setSavingLocation(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="dependants-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">
              {role === 'subscriber' ? 'My Dependants' : 'All Dependants'}
            </h1>
            <p className="text-gray-500 mt-1">
              {dependants.length} dependant{dependants.length !== 1 ? 's' : ''} enrolled
            </p>
          </div>
          {role === 'subscriber' && (
            <Button asChild className="rounded-full">
              <Link to="/dependants/add">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Dependant
              </Link>
            </Button>
          )}
        </div>

        {missingLocationPins.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3 text-amber-900">
                <MapPin className="mt-0.5 h-5 w-5" />
                <div>
                  <p className="font-medium">Confirm home location pins</p>
                  <p className="text-sm">GPS anomaly checks need exact home coordinates for {missingLocationPins.length} dependant{missingLocationPins.length === 1 ? '' : 's'}.</p>
                </div>
              </div>
              <Button type="button" variant="outline" onClick={() => openLocationModal(missingLocationPins[0])}>
                Confirm first location
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dependants.map(dependant => (
            <div key={dependant.id} className="space-y-3">
              {!hasHomePin(dependant) && (
                <button
                  type="button"
                  onClick={() => openLocationModal(dependant)}
                  className="flex w-full items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm font-medium text-amber-900"
                >
                  <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />Confirm home location</span>
                </button>
              )}
              {role === 'subscriber' && (
                <Button asChild variant="outline" className="w-full justify-center rounded-2xl">
                  <Link to={`/dependants/${dependant.id}/edit`}>
                    <Edit3 className="mr-2 h-4 w-4" />
                    Edit dependant info
                  </Link>
                </Button>
              )}
              <DependantCard
                dependant={dependant}
                onNameClick={role === 'admin' && (dependant.userId || dependant.user_id)
                  ? () => setDetailUserId(dependant.userId || dependant.user_id)
                  : undefined}
              />
            </div>
          ))}
        </div>
      </div>

      {selectedDependant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Confirm Home Location</h2>
                <p className="text-sm text-[#5A5A5A]">{selectedDependant.firstName} {selectedDependant.lastName}</p>
              </div>
              <Button type="button" variant="outline" onClick={() => setSelectedDependant(null)}>Close</Button>
            </div>
            <GoogleMapLocationPicker
              value={locationDraft}
              addressHint={addressHintFor(selectedDependant)}
              onChange={setLocationDraft}
              required
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setSelectedDependant(null)}>Cancel</Button>
              <Button type="button" onClick={saveLocation} disabled={savingLocation || !locationDraft?.coordinates}>
                {savingLocation ? 'Saving...' : 'Save location'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />
    </div>
  );
};

export default DependantsPage;
